import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { IUser } from '@/types';
import { UserRole } from '@/types';

interface IAuthState {
  user: IUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setUser: (user: IUser) => void;
  clearUser: () => void;
  setLoading: (loading: boolean) => void;

  // Role checks
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasPermission: (permission: string) => boolean;
}

export const useAuthStore = create<IAuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user: IUser) =>
        set({ user, isAuthenticated: true, isLoading: false }),

      clearUser: () =>
        set({ user: null, isAuthenticated: false, isLoading: false }),

      setLoading: (isLoading: boolean) => set({ isLoading }),

      hasRole: (role: string) => {
        const { user } = get();
        if (!user) return false;
        if (user.role === UserRole.SYSADMIN) return true;
        return user.role === role;
      },

      hasAnyRole: (roles: string[]) => {
        const { user } = get();
        if (!user) return false;
        if (user.role === UserRole.SYSADMIN) return true;
        return roles.includes(user.role);
      },

      hasPermission: (permission: string) => {
        const { user } = get();
        if (!user) return false;
        if (user.role === UserRole.SYSADMIN) return true;
        return user.permissions.includes(permission);
      },
    }),
    {
      name: 'at360-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // After zustand restores persisted state, clear the loading flag.
        // If user was persisted, they're authenticated until token validation fails.
        // If no user was persisted, they need to login.
        state?.setLoading(false);
      },
    }
  )
);
